import memoize from 'lodash/memoize';
import {
	type Action,
	type BoundActions,
	createActionsHook,
	createContainer,
	createHook,
	createSelector,
	createStore,
	type HookActionsFunction,
	type HookFunction,
	type OverrideContainerComponent,
} from 'react-sweet-state';

import { userQuery } from '@atlassian/dynamic-cards-search-client';

import type { PrimaryProductKey, ProductKeys } from '../../../common/constants/products';
import {
	type SearchQueryStringParams,
	type SearchQueryStringParamsRaw,
} from '../../../common/constants/schemas/query-params';
import {
	type ExperimentConfig,
	type onInputDropdownResultSelect,
	type onInputSubmit,
	type UserDetailsWithPicture,
	type UserDetailsWithStatus,
	UserStatus,
} from '../../../common/constants/types';
import { type Experiment, type OutboundAuthConfigsType } from '../../../common/types';
import {
	areAllEqualQueryParams,
	parseQueryParams,
	toQueryParamsRaw,
	toQueryString,
} from '../../../common/utils/query-params';

const HELLO_TENANT_ID = 'a436116f-02ce-4520-8fbb-7301462a1674';

export type Features = {
	// Enables auto suggestion look up
	autoSuggestion?: { enabled: boolean };
	chromeExtension?: {
		enabled: boolean;
		onInputDropdownResultSelect: onInputDropdownResultSelect;
		onInputSubmit: onInputSubmit;
	};
	// Engagement platform related features like IPMs
	engagement?: { enabled: boolean };

	// Makes an initial search request based on provided queryparams. Needed for full search page to populate the initial result list.
	fetchInitialResults?: { enabled: boolean };

	// Enables the inline search button which triggers a new search via queryparams update when clicked
	inlineSearch?: { enabled: boolean };

	// Enables people and teams results in menu and also does prefetching
	peopleAndTeamsSearch?: { enabled: boolean };

	// Enables the quick search which returns results based on your search and filters
	quickSearch?: { enabled: boolean };

	// Enables the list of my recently searched keywords
	recentSearches?: { enabled: boolean };

	// Enables the search items from my recent activities which match the search keyword
	recentViews?: { enabled: boolean };

	sideBySide?: {
		/**
		 * Whether the side-by-side layout is enabled.
		 */
		enabled: boolean;
		/**
		 * Experiments specifying the treatments to be implemented.
		 */
		experiments?: Experiment[];
	};
};

type FeatureKey = keyof Features;

// Component replacements and customizations
export type Components = {
	footer?: React.ReactElement | ((moreURL: string) => React.ReactElement);
};

export type StyleOverrides = {
	// A combination of CSS and HTML styling overrrides for any component
	// For CSS overrides, only a selective set is exposed to props API to be cautious
	textfield?: {
		backgroundColor?: string;
		borderRadius?: string;
		placeholder?: string;
		textColor?: string;
	};
};

export type TState = {
	aggAbsoluteUrl?: string;
	aggregatorHostName?: string;
	allFilterExcludedProducts: ProductKeys[];
	analyticsAttributes?: Record<string, any>;
	cloudId?: string;
	components?: Components;
	experience?: string;
	experimentConfig: ExperimentConfig;
	features?: Features;
	/**
	 * The ARIs for the spaces parsed from query params.
	 * This was added to support the query param as a space key
	 * while most backend systems require ARIs to operate.
	 **/
	initialSpaceARIs: string[];
	intercomHmac?: string;
	isAdminHubAIEnabled?: boolean;
	isNav4Enabled?: boolean;
	isNounsAvailable: boolean;
	orgId?: string;
	outboundAuthConfigs: OutboundAuthConfigsType;
	prevQueryParams: SearchQueryStringParams;
	primaryProduct?: PrimaryProductKey;
	products: ProductKeys[];
	queryParams: SearchQueryStringParams;
	referrer?: string;
	SAINHostName?: string;
	searchSessionId: string;
	styleOverrides?: StyleOverrides;
	user?: UserDetailsWithPicture;
};

/**
 * This state object contains all the configuration which must be loaded to make the page functional.
 */
export const initialState: {
	allFilterExcludedProducts: never[];
	experimentConfig: {
		backendExperiment: undefined;
		frontendExperiments: undefined;
	};
	features: {
		autoSuggestion: {
			enabled: true;
		};
		engagement: {
			enabled: true;
		};
		fetchInitialResults: {
			enabled: true;
		};
		inlineSearch: {
			enabled: true;
		};
		peopleAndTeamsSearch: {
			enabled: true;
		};
		// These feature defaults are included to be backwards-compatible with initial search implementation forked from search pkg
		quickSearch: {
			enabled: false;
		};
		recentSearches: {
			enabled: true;
		};
		recentViews: {
			enabled: true;
		};
	};
	initialSpaceARIs: never[];
	isNounsAvailable: false;
	outboundAuthConfigs: {};
	prevQueryParams: {};
	products: never[];
	queryParams: {};
	searchSessionId: string;
} = {
	products: [],
	allFilterExcludedProducts: [],
	outboundAuthConfigs: {},
	searchSessionId: 'searchSessionId',
	queryParams: {},
	prevQueryParams: {},
	initialSpaceARIs: [],
	experimentConfig: {
		backendExperiment: undefined,
		frontendExperiments: undefined,
	},
	isNounsAvailable: false,
	features: {
		// These feature defaults are included to be backwards-compatible with initial search implementation forked from search pkg
		quickSearch: { enabled: false },
		inlineSearch: { enabled: true },
		autoSuggestion: { enabled: true },
		recentSearches: { enabled: true },
		recentViews: { enabled: true },
		peopleAndTeamsSearch: { enabled: true },
		fetchInitialResults: { enabled: true },
		engagement: { enabled: true },
	},
} satisfies TState;

export const actions = {
	setState:
		(partialState: Partial<TState>): Action<TState> =>
		({ setState }) => {
			setState(partialState);
		},
	loadUserPicture:
		(): Action<TState> =>
		({ setState, getState }) => {
			const userState = getState().user;
			const userId = userState?.id;

			if (!userState || !userId || !!userState.picture) {
				return;
			}

			userQuery(userId)
				.then((x) => {
					setState({
						user: {
							id: userState.id,
							name: userState.name,
							email: userState.email,
							picture: x.data?.user.picture ?? null,
						},
					});
				})
				.catch((_) => {
					// TODO: >>>> Send warning to sentry
				});
		},
	setQueryParams:
		(queryParam: Partial<SearchQueryStringParams>): Action<TState, BootstrapContainerProps> =>
		({}, { queryParams, queryParamsCallback = () => {} }) => {
			const areEqualQueryParams = areAllEqualQueryParams(
				queryParam,
				parseQueryParams(queryParams ?? {}),
			);

			if (areEqualQueryParams) {
				return;
			}

			queryParamsCallback(toQueryParamsRaw(queryParam));
		},
	syncQueryParams:
		(): Action<TState, BootstrapContainerProps> =>
		({ setState, getState }, { queryParams, queryParamsCallback = () => {} }) => {
			// queryParams is the query params coming from the URL or React Router in this case
			const {
				prevQueryParams, // Previously saved query params - this is to prevent infinitely updating queryParams
				queryParams: diffQueryParamsState, // The 'internal' query params state
			} = getState();

			const queryParamsURL = parseQueryParams(queryParams ?? {});

			const prevQueryParamsString = toQueryString(prevQueryParams);
			const diffQueryParamsURLString = toQueryString(queryParamsURL);
			const diffQueryParamsStateString = toQueryString(diffQueryParamsState);

			const allQueryParamsAreEqual = areAllEqualQueryParams(
				queryParamsURL,
				prevQueryParams,
				diffQueryParamsState,
			);

			if (allQueryParamsAreEqual) {
				return;
			}

			// Any changes from the url takes the highest precedence
			const hasURLQueryParams = !!queryParams;
			if (hasURLQueryParams && prevQueryParamsString !== diffQueryParamsURLString) {
				setState({
					prevQueryParams: queryParamsURL,
					queryParams: queryParamsURL,
				});
				return;
			}

			if (prevQueryParamsString !== diffQueryParamsStateString) {
				//  NOTE: If you are running the callback and you don't see the query params being updated,
				//  the callback might be sanitising the query parameters. This is the case for Confluence.
				queryParamsCallback(toQueryParamsRaw(diffQueryParamsState));
				setState({
					prevQueryParams: diffQueryParamsState,
					queryParams: diffQueryParamsState,
				});
				return;
			}
		},
	initializeSpaceARIsFromSpaceKeys:
		(spaceKeys: string[]): Action<TState, BootstrapContainerProps> =>
		async ({ setState, getState }, { getSpaceIdsFromSpaceKeys }) => {
			const { cloudId } = getState();
			if (spaceKeys.length === 0 || !getSpaceIdsFromSpaceKeys) {
				return;
			}
			const spaceIds = await getSpaceIdsFromSpaceKeys(spaceKeys);
			const spaceARIs = spaceIds.map((id) => `ari:cloud:confluence:${cloudId}:space/${id}`);
			setState({ initialSpaceARIs: spaceARIs });
		},
	clearSpaceARIs:
		(): Action<TState, BootstrapContainerProps> =>
		({ setState }) => {
			setState({ initialSpaceARIs: [] });
		},
};

type TActions = typeof actions;

// Keep store private and use hooks to access state and actions
const Store = createStore<TState, TActions>({
	initialState,
	actions,
	name: 'dynamic-cards-bootstrap',
});

export type BootstrapContainerProps = {
	/**
	 * @returns the space IDs for the given space keys. NOTE: these should be the space IDs, not ARIs.
	 * @example getSpaceIdsFromSpaceKeys(["STATUS"]); // Promise<["123454321"]>
	 **/
	getSpaceIdsFromSpaceKeys?: (spaceKeys: string[]) => Promise<string[]>;
	initialProps: Partial<TState>;
	queryParams?: Record<string, any>;
	/** Called when FPS query params should be updated in the application's router. */
	queryParamsCallback?: (queryParams: Partial<SearchQueryStringParamsRaw>) => void;
};

const buildProducts = memoize(
	(a: ProductKeys[], b: ProductKeys[]) => {
		return [...new Set([...a, ...b])];
	},
	(a: ProductKeys[], b: ProductKeys[]) => a.join('') + b.join(''),
);

// Merge features while ensuring defaults from initialState are maintained and
// only overridden if explicitly set by container props
export const buildFeatureSet = (initialState: TState, initialProps: Partial<TState>): Features => {
	const defaultFeatures = initialState.features || {};
	const overrideFeatures = initialProps.features || {};
	// TODO: Fix any
	const mergedFeatures: any = { ...defaultFeatures };

	for (const key in overrideFeatures) {
		const typedKey = key as FeatureKey;
		if (overrideFeatures[typedKey] !== undefined) {
			mergedFeatures[typedKey] = {
				...defaultFeatures[typedKey],
				...overrideFeatures[typedKey],
			};
		}
	}

	return mergedFeatures;
};

export const BootstrapStoreContainer: OverrideContainerComponent<BootstrapContainerProps> =
	createContainer<TState, TActions, BootstrapContainerProps>(Store, {
		onInit:
			() =>
			({ getState, setState, dispatch }, { initialProps, queryParams }) => {
				const parsedQueryParams = queryParams && parseQueryParams(queryParams);
				const initialState = getState();
				const initState = {
					...initialState,
					...initialProps,
					...(queryParams && { queryParams: parsedQueryParams }),
					referrer: parsedQueryParams?.referrer,
					features: buildFeatureSet(initialState, initialProps),
				};
				setState(initState);
				dispatch(actions.loadUserPicture());
			},
		onUpdate:
			() =>
			({ setState, getState, dispatch }, { initialProps, queryParams }) => {
				const parsedQueryParams = queryParams && parseQueryParams(queryParams);
				const initialState = getState();
				const products = buildProducts(initialState.products, initialProps?.products ?? []);
				setState({
					...initialState,
					...initialProps,
					products,
					referrer: parsedQueryParams?.referrer,
					features: buildFeatureSet(initialState, initialProps),
				});
				if (parsedQueryParams?.spaces) {
					dispatch(actions.initializeSpaceARIsFromSpaceKeys(parsedQueryParams.spaces));
				} else {
					dispatch(actions.clearSpaceARIs());
				}
				dispatch(actions.syncQueryParams());
			},
	});

// Hooks

// Note - This doesnt trigger a re-render
export const useBootstrapActions: HookActionsFunction<
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>
> = createActionsHook(Store);

export const useBootstrap: HookFunction<
	TState,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store);

/**
 * This hook gets the extra analytics attributes that are passed in as a prop from the consumer.
 * It should not be confused with the hooks for firing analytics events in `controllers/use-analytics`
 */
export const useAnalyticsAttributes: HookFunction<
	Record<string, any> | undefined,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.analyticsAttributes,
});

export const useAvailableProducts: HookFunction<
	ProductKeys[],
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.products,
});

export const useBootstrapLoading: HookFunction<
	boolean,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.products.length === 0,
});

export const usePrimaryProduct: HookFunction<
	PrimaryProductKey | undefined,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.primaryProduct,
});

export const useSearchSessionId: HookFunction<
	string,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.searchSessionId,
});

export const useIsHello: HookFunction<
	boolean,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.cloudId === HELLO_TENANT_ID,
});

export const useFeatures: HookFunction<
	Features | undefined,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.features,
});

export const useComponents: HookFunction<
	Components | undefined,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.components,
});

export const useStyleOverrides: HookFunction<
	StyleOverrides | undefined,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.styleOverrides,
});

export const useExperimentConfig: HookFunction<
	ExperimentConfig,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.experimentConfig,
});

export const useBackendExperiment: HookFunction<
	any,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.experimentConfig.backendExperiment,
});

export const useFrontendExperiments: HookFunction<
	any,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.experimentConfig.frontendExperiments,
});

export const useIntercomHmac: HookFunction<
	string | undefined,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.intercomHmac,
});

export const useIsNounsAvailable: HookFunction<
	boolean,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.isNounsAvailable,
});

export const useUser: HookFunction<
	UserDetailsWithStatus,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state): UserDetailsWithStatus => {
		const user = state.user satisfies UserDetailsWithPicture | undefined;
		if (!user || !user.id) {
			return {
				status: UserStatus.Unauthenticated,
				id: null,
				name: null,
				email: null,
			};
		}

		if (user.id === 'unidentified') {
			return {
				status: UserStatus.Anonymous,
				...user,
			};
		}

		return {
			status: UserStatus.Authenticated,
			...user,
		};
	},
});

export const useReferrer: HookFunction<
	string | undefined,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.referrer,
});

export const useOrgId: HookFunction<
	string | undefined,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.orgId,
});

export const useCloudId: HookFunction<
	string | undefined,
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: createSelector([(state) => state.cloudId], (cloudId) => cloudId),
});

export const useQueryParams: HookFunction<
	{
		ancestor?: string | undefined;
		contributors?: string[] | undefined;
		from?: string | undefined;
		jAssignee?: string[] | undefined;
		jProject?: string[] | undefined;
		labels?: string[] | undefined;
		lastModified?:
			| 'custom'
			| 'anytime'
			| 'today'
			| 'yesterday'
			| 'past7Days'
			| 'past30Days'
			| 'pastYear'
			| undefined;
		lastModified_gt?: Date | undefined;
		lastModified_lt?: Date | undefined;
		originalQuery?: string | undefined;
		owners?: string[] | undefined;
		product?:
			| 'confluence'
			| 'townsquare'
			| 'jira'
			| 'asana'
			| 'bitbucket'
			| 'compass'
			| 'drive'
			| 'jira-product-discovery'
			| 'jira-servicedesk'
			| 'opsgenie'
			| 'sharepoint'
			| 'slack'
			| 'trello'
			| 'atlassian-web-crawler'
			| 'teams'
			| 'figma'
			| 'github'
			| 'loom'
			| 'onedrive'
			| 'google-calendar'
			| 'dropbox'
			| 'gmail'
			| 'outlook-calendar'
			| 'airtable'
			| 'zendesk'
			| 'notion'
			| 'miro'
			| undefined;
		referrer?: string | undefined;
		sortField?: 'latestModifiedDate' | 'createdDate' | undefined;
		sortOrder?: 'ASC' | 'DESC' | undefined;
		spaces?: string[] | undefined;
		text?: string | undefined;
		titleMatch?: boolean | undefined;
		to?: string | undefined;
		type?: string | undefined;
		verified?: boolean | undefined;
	},
	BoundActions<
		TState,
		{
			clearSpaceARIs: () => Action<TState, BootstrapContainerProps>;
			initializeSpaceARIsFromSpaceKeys: (
				spaceKeys: string[],
			) => Action<TState, BootstrapContainerProps>;
			loadUserPicture: () => Action<TState>;
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<TState, BootstrapContainerProps>;
			setState: (partialState: Partial<TState>) => Action<TState>;
			syncQueryParams: () => Action<TState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.queryParams,
});
